import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Button, LightBulbOutline } from '@bonusly/design-system';
import { Permission, useCurrentCompany, useCurrentUser, useFeatureFlag, usePermission } from '@bonusly/who-am-i';

import { useBizyPromptSuggestions, type BizyPromptSuggestion } from '../../../queries/bizy';
import { useGithubStatus } from '../../../queries/github';
import { useGranolaStatus } from '../../../queries/granola';
import { useLinearStatus } from '../../../queries/linear';
import { BizyModelSelector } from '../components/bizy-model-selector';
import { BizyPromptLibraryMenu } from '../components/bizy-prompt-library-menu';
import { BizyPromptLibraryModal, type BizyPromptLibraryModalProps } from '../components/bizy-prompt-library-modal';
import {
	getPromptLibraryCategories,
	loadFavoritePromptKeys,
	saveFavoritePromptKeys,
	toggleFavoritePromptKey,
	type PromptConnector,
	type PromptLibraryPrompt,
} from '../lib/prompt-library';
import { usePromptLibraryModalStore } from './use-prompt-library-modal-store';
import { usePromptLibraryPartnerName } from './use-prompt-library-partner-name';

// Shared stable reference for the empty-suggestions case (see usage below).
const EMPTY_PROMPT_SUGGESTIONS: BizyPromptSuggestion[] = [];

/**
 * Renders the modal while subscribing to its open-state HERE rather than in the
 * chat view. Because this host owns the `isOpen` subscription, toggling the
 * modal re-renders only this tiny component (and the modal), never the
 * ~1,900-line BizySidePanelChatView that renders it. See use-prompt-library-modal-store.
 */
function PromptLibraryModalHost(props: Omit<BizyPromptLibraryModalProps, 'isOpen'>) {
	const isOpen = usePromptLibraryModalStore((state) => state.isOpen);
	const close = usePromptLibraryModalStore((state) => state.close);
	// The open flag lives in a module-level store (so toggling it doesn't
	// re-render the chat view). Reset it when this host unmounts so the flag can't
	// outlive its host — otherwise a chat-view remount (e.g. the fullscreen layout
	// toggle re-mounts BizySidePanelChatView) would re-open a modal that was left
	// open, and it would leak between tests. Mirrors the old useDisclosure
	// local-state lifecycle.
	useEffect(() => () => close(), [close]);
	return <BizyPromptLibraryModal isOpen={isOpen} {...props} />;
}

export interface UseBizyPromptLibraryOptions {
	/** Page type for analytics context and page-aware filtering. Omit in surface-agnostic contexts (e.g. desktop). */
	pageType?: string;
	/** Page name for partner-name resolution and analytics. */
	pageName?: string;
	/** Disables all prompt library buttons/menus. */
	isDisabled?: boolean;
	/** Called when a prompt is chosen (chip, menu item, or library pick). The hook handles message sending via this. */
	onSendMessage: (message: string, extraContext?: Record<string, string>) => void;
	/** Optional: called after a prompt is sent — use to scroll to bottom or re-focus the composer. */
	onAfterSelect?: () => void;
	/** Optional: called when the user wants to connect a connector (e.g. settings > connections). */
	onShowSettings?: (tab: string) => void;
	/**
	 * When provided, renders the model selector in the footer.
	 * The caller manages the selected model state (to avoid a circular dependency with the chat hook).
	 */
	modelSelector?: {
		selectedModel: string;
		onSelectModel: (model: string) => void;
	};
}

export interface UseBizyPromptLibraryResult {
	/** The prompt library menu + optional model selector for the composer's footerLeft slot. */
	footerContent: React.ReactNode;
	/** The BizyPromptLibraryModal; render this alongside (not inside) BizyComposer. */
	modal: React.ReactNode;
	/** Personalized prompt suggestions — expose for callers that also use them for prompt chips. */
	personalizedPromptSuggestions: BizyPromptSuggestion[];
	/** True when personalized suggestions have resolved (or suggestions are disabled). */
	isPromptSuggestionsReady: boolean;
}

export function useBizyPromptLibrary({
	pageType,
	pageName,
	isDisabled = false,
	onSendMessage,
	onAfterSelect,
	onShowSettings,
	modelSelector,
}: UseBizyPromptLibraryOptions): UseBizyPromptLibraryResult {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();

	// Identity
	const user = useCurrentUser();
	const company = useCurrentCompany();
	const isCanceled = company.status === 'non_renewing';

	// Model experiment flag — only needed to control the prompt library button label.
	const isBizyModelExperimentEnabled = useFeatureFlag('bizy-model-experiment');

	// Personalized suggestions
	const { data: promptSuggestionsData, isFetched: isPromptSuggestionsFetched } = useBizyPromptSuggestions(!isCanceled);
	// Stable empty fallback: `?? []` would mint a fresh array every render, which
	// (as a dependency of the catalog memo below) would defeat memoization and
	// rebuild the whole prompt catalog on every render.
	const personalizedPromptSuggestions = promptSuggestionsData?.prompt_suggestions ?? EMPTY_PROMPT_SUGGESTIONS;
	const isPromptSuggestionsReady = isCanceled || isPromptSuggestionsFetched;

	// Permissions
	const canGiveRecognition = usePermission(Permission.CanGiveRecognition);
	const canAccessOneOnOnes = usePermission(Permission.CanAccessOneOnOnes);
	const canAccessTeamHub = usePermission(Permission.CanAccessTeamHub);
	const canAccessGoals = usePermission(Permission.CanAccessGoals);
	const canAccessAdvice = usePermission(Permission.CanAccessAdvice);
	const canAccessCheckIn = usePermission(Permission.CanAccessCheckIn);
	const canAccessIncentives = usePermission(Permission.CanAccessIncentives);
	const canAccessNominations = usePermission(Permission.CanAccessNominations);
	const canAccessAnnouncements = usePermission(Permission.CanAccessAnnouncements);
	const canAdministerAnnouncements = usePermission(Permission.CanAdministerAnnouncements);
	const canAccessUserManagement = usePermission(Permission.CanAccessUserManagement);
	const canManageUserImport = usePermission(Permission.CanManageUserImport);
	const canManagePeerToPeer = usePermission(Permission.CanManagePeerToPeer);
	const canAccessAnalytics = usePermission(Permission.CanAccessAnalytics);
	const canViewAdmin = usePermission(Permission.CanViewAdmin);

	// Connector status
	const isAdmin = user.admin;
	const hasGranolaConnector = Boolean(company.bizy_connectors_granola);
	const hasLinearConnector = Boolean(company.bizy_connectors_linear);
	const hasGithubConnector = Boolean(company.bizy_connectors_github);
	const { data: granolaStatus } = useGranolaStatus(hasGranolaConnector);
	const { data: linearStatus } = useLinearStatus(hasLinearConnector);
	const { data: githubStatus } = useGithubStatus(hasGithubConnector);

	const promptLibraryGates = useMemo(
		() => ({
			isAdmin,
			canGiveRecognition,
			canAccessOneOnOnes,
			canAccessTeamHub,
			canAccessGoals,
			canAccessAdvice,
			canAccessCheckIn,
			canAccessIncentives,
			canAccessNominations,
			canAccessAnnouncements,
			canAdministerAnnouncements,
			canAccessUserManagement,
			canManageUserImport,
			canManagePeerToPeer,
			canAccessAnalytics,
			canViewAdmin,
			hasGranolaConnector,
			hasLinearConnector,
			hasGithubConnector,
			isGranolaConnected: granolaStatus ? granolaStatus.status === 'connected' : undefined,
			isLinearConnected: linearStatus ? linearStatus.status === 'connected' : undefined,
			isGithubConnected: githubStatus ? githubStatus.status === 'connected' : undefined,
		}),
		[
			isAdmin,
			canGiveRecognition,
			canAccessOneOnOnes,
			canAccessTeamHub,
			canAccessGoals,
			canAccessAdvice,
			canAccessCheckIn,
			canAccessIncentives,
			canAccessNominations,
			canAccessAnnouncements,
			canAdministerAnnouncements,
			canAccessUserManagement,
			canManageUserImport,
			canManagePeerToPeer,
			canAccessAnalytics,
			canViewAdmin,
			hasGranolaConnector,
			hasLinearConnector,
			hasGithubConnector,
			granolaStatus?.status,
			linearStatus?.status,
			githubStatus?.status,
		]
	);

	// Partner name (page-aware prompt personalisation)
	const promptLibraryPartnerName = usePromptLibraryPartnerName(pageType, pageName);
	const promptLibraryName = promptLibraryPartnerName ?? 'someone on my team';

	// Modal open/close actions. Selecting only the (stable) actions — never
	// `isOpen` — keeps this hook, and the chat view that calls it, from
	// re-rendering when the modal toggles. The modal host owns that subscription.
	const openPromptLibrary = usePromptLibraryModalStore((state) => state.open);
	const closePromptLibrary = usePromptLibraryModalStore((state) => state.close);

	// Favorites
	const [favoritePromptKeys, setFavoritePromptKeys] = useState<string[]>(() => loadFavoritePromptKeys(user.id));
	useEffect(() => {
		setFavoritePromptKeys(loadFavoritePromptKeys(user.id));
	}, [user.id]);

	// Derived lists
	const promptLibraryCategories = useMemo(
		() => getPromptLibraryCategories(promptLibraryGates, personalizedPromptSuggestions),
		[promptLibraryGates, personalizedPromptSuggestions]
	);
	// Derive favourites from the already-built catalog. The previous
	// getFavoritePromptLibraryPrompts helper rebuilt the ENTIRE catalog once per
	// favourite key (O(favourites × catalog)); combined with the many re-renders
	// as permission/connector queries resolve, that rebuilt the catalog dozens of
	// times per load. Here we build once (above) and look keys up from a map.
	const favoritePromptLibraryPrompts = useMemo(() => {
		const promptsByKey = new Map(promptLibraryCategories.flatMap((category) => category.prompts).map((prompt) => [prompt.key, prompt]));
		return (
			favoritePromptKeys
				.map((key) => promptsByKey.get(key))
				// Drop locked previews: a connector prompt the user can't send yet
				// shouldn't appear as a one-click favourite in the menu.
				.filter((prompt): prompt is PromptLibraryPrompt => prompt != null && !prompt.locked)
		);
	}, [favoritePromptKeys, promptLibraryCategories]);

	// Handlers
	const handleOpenPromptLibrary = useCallback(() => {
		track(AnalyticsEvent.Bizy.PromptLibraryOpened, {
			path: window.location.pathname,
			pageType,
			pageName,
			trigger: 'input_footer',
		});
		openPromptLibrary();
	}, [openPromptLibrary, pageName, pageType, track]);

	const handlePromptLibraryMenuOpen = useCallback(() => {
		track(AnalyticsEvent.Bizy.PromptLibraryMenuOpened, {
			path: window.location.pathname,
			pageType,
			favoriteCount: favoritePromptLibraryPrompts.length,
		});
	}, [favoritePromptLibraryPrompts.length, pageType, track]);

	const handleClosePromptLibrary = useCallback(() => {
		track(AnalyticsEvent.Bizy.PromptLibraryClosed, { path: window.location.pathname, pageType });
		closePromptLibrary();
	}, [closePromptLibrary, pageType, track]);

	const handleToggleFavoritePrompt = useCallback(
		(prompt: PromptLibraryPrompt) => {
			const nextKeys = toggleFavoritePromptKey(favoritePromptKeys, prompt.key);
			setFavoritePromptKeys(nextKeys);
			saveFavoritePromptKeys(user.id, nextKeys);
			track(AnalyticsEvent.Bizy.PromptLibraryFavoriteToggled, {
				path: window.location.pathname,
				promptKey: prompt.key,
				categoryId: prompt.categoryId,
				isFavorite: nextKeys.includes(prompt.key),
			});
		},
		[favoritePromptKeys, track, user.id]
	);

	const handleSelectLibraryPrompt = useCallback(
		(prompt: PromptLibraryPrompt, promptText: string, source: 'library' | 'menu' = 'library') => {
			const isFeatureRequestPrompt = prompt.key === 'promptLibrary.library.getHelp.featureRequest';
			track(AnalyticsEvent.Bizy.PromptLibraryPromptSelected, {
				path: window.location.pathname,
				promptKey: prompt.key,
				categoryId: prompt.categoryId,
				source,
				pageType,
			});
			onSendMessage(promptText, {
				...(isFeatureRequestPrompt ? { source: 'feature_request', source_page_path: window.location.pathname } : {}),
				prompt_source: source,
				prompt_key: prompt.key,
				prompt_category: prompt.categoryId,
			});
			if (source === 'library') {
				closePromptLibrary();
			}
			onAfterSelect?.();
		},
		[pageType, closePromptLibrary, onSendMessage, onAfterSelect, track]
	);

	const handleSelectMenuPrompt = useCallback(
		(prompt: PromptLibraryPrompt, promptText: string) => {
			handleSelectLibraryPrompt(prompt, promptText, 'menu');
		},
		[handleSelectLibraryPrompt]
	);

	const handleConnectConnector = useCallback(
		(connector: PromptConnector) => {
			track(AnalyticsEvent.Bizy.PromptLibraryConnectorClicked, {
				path: window.location.pathname,
				connector,
				pageType,
			});
			closePromptLibrary();
			onShowSettings?.('connections');
		},
		[pageType, closePromptLibrary, onShowSettings, track]
	);

	const handleSurpriseMe = useCallback(() => {
		track(AnalyticsEvent.Bizy.PromptLibrarySurpriseMe, { path: window.location.pathname, pageType });
	}, [pageType, track]);

	// Rendered nodes
	const footerContent = (
		<>
			{favoritePromptLibraryPrompts.length > 0 ? (
				<BizyPromptLibraryMenu
					favoritePrompts={favoritePromptLibraryPrompts}
					onSelectPrompt={handleSelectMenuPrompt}
					onBrowsePrompts={handleOpenPromptLibrary}
					onOpenMenu={handlePromptLibraryMenuOpen}
					promptName={promptLibraryName}
					isDisabled={isDisabled}
					showLabel={!isBizyModelExperimentEnabled}
				/>
			) : (
				<Button
					size="xs"
					variant="ghost"
					leftIcon={<LightBulbOutline boxSize={3.5} />}
					bg="secondary.bgAlpha"
					borderRadius="6px"
					px={1.5}
					py={0.5}
					h="auto"
					minH="24px"
					color="text.soft"
					fontWeight="normal"
					isDisabled={isDisabled}
					aria-label={isBizyModelExperimentEnabled ? t('promptLibrary.menuButton') : undefined}
					iconSpacing={isBizyModelExperimentEnabled ? 0 : undefined}
					_hover={{ bg: 'secondary.bgAlphaHover', color: 'text.default' }}
					onClick={handleOpenPromptLibrary}
				>
					{isBizyModelExperimentEnabled ? null : t('promptLibrary.menuButton')}
				</Button>
			)}
			{modelSelector && <BizyModelSelector selectedModel={modelSelector.selectedModel} onSelectModel={modelSelector.onSelectModel} isDisabled={isDisabled} />}
		</>
	);

	const modal = (
		<PromptLibraryModalHost
			onClose={handleClosePromptLibrary}
			categories={promptLibraryCategories}
			favoriteKeys={favoritePromptKeys}
			onToggleFavorite={handleToggleFavoritePrompt}
			onSelectPrompt={handleSelectLibraryPrompt}
			onConnectConnector={handleConnectConnector}
			onSurpriseMe={handleSurpriseMe}
			promptName={promptLibraryName}
		/>
	);

	return {
		footerContent,
		modal,
		personalizedPromptSuggestions,
		isPromptSuggestionsReady,
	};
}
